import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MessageSquareWarning } from 'lucide-react';
import api from '../lib/api';
import { when } from './PlatformConsole';
import { OnlineOnlyNotice } from '../components/OnlineOnlyNotice';
import { useOnlineStatus } from '../lib/useOnlineStatus';

const RecallSmsDialog = ({ terms, onClose, onSent }) => {
  const [includeAdr, setIncludeAdr] = useState(true);
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setConfirmed(false);
    api.get('/platform/patients/recall/notify-preview', { params: { ...terms, include_adr: includeAdr } })
      .then(({ data }) => { setPreview(data); setMessage((m) => m || data.default_message); })
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not count recipients'));
  }, [terms, includeAdr]);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/platform/patients/recall/notify', {
        ...terms, include_adr: includeAdr, message, expected_recipients: preview.recipients });
      toast.success(`Recall text is going out to ${data.recipients} patient(s)`);
      onSent(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send recall texts'); }
    finally { setBusy(false); }
  };

  const ready = preview && preview.sms_ready && preview.recipients > 0;
  return (
    <div className="pc-modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pc-modal" data-testid="pc-recall-sms-dialog">
        <h3>Text patients about this recall</h3>
        {!preview ? <p className="pc-muted">Counting patients…</p> : (
          <>
            <p className="pc-sub" data-testid="pc-recall-sms-count">
              {preview.recipients} patient(s) with a valid phone will get this text
              {preview.no_phone > 0 && ` · ${preview.no_phone} record(s) have no usable phone`}.
              Sent from the platform account{preview.sender_id && ` as “${preview.sender_id}”`}.
            </p>
            {!preview.sms_ready && <p className="pc-muted" style={{ color: '#ff9b9b' }} data-testid="pc-recall-sms-not-ready">Platform SMS isn't set up yet — turn it on in Messaging first.</p>}
            {preview.last_campaign && <p className="pc-muted" data-testid="pc-recall-sms-last">Already texted {preview.last_campaign.recipients} patient(s) about this on {when(preview.last_campaign.created_at)}.</p>}
            <label className="pc-field" style={{ marginTop: '.6rem', display: 'flex', gap: '.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={includeAdr} onChange={(e) => setIncludeAdr(e.target.checked)} data-testid="pc-recall-sms-include-adr" />
              Include patients who reported a reaction
            </label>
            <label className="pc-field" style={{ marginTop: '.6rem' }}>Message ({message.length}/480)</label>
            <textarea className="pc-textarea" rows={5} maxLength={480} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="pc-recall-sms-message" />
            <label className="pc-field" style={{ marginTop: '.6rem', display: 'flex', gap: '.4rem', alignItems: 'center' }}>
              <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="pc-recall-sms-confirm" />
              I confirm this recall is real and the message is correct
            </label>
            <p className="pc-muted" style={{ marginTop: '.5rem', color: '#ffcf72' }} data-testid="pc-recall-sms-lock-note">
              Sending also blocks {terms.batch ? `batch ${terms.batch}` : 'every batch of this medicine'} from sale at every pharmacy, with no override, until you lift the lock.
            </p>
          </>
        )}
        <div style={{ display: 'flex', gap: '.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
          <button type="button" className="pc-btn" onClick={onClose} data-testid="pc-recall-sms-cancel">Cancel</button>
          <button type="button" className="pc-btn pc-btn-primary" disabled={!ready || !confirmed || busy || message.trim().length < 20 || !navigator.onLine}
            onClick={send} data-testid="pc-recall-sms-send">{busy ? 'Sending…' : `Send to ${preview?.recipients ?? 0}`}</button>
        </div>
      </div>
    </div>
  );
};

const StockCount = ({ lock }) => {
  const online = useOnlineStatus();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (data) return;
    setBusy(true);
    try {
      const { data: res } = await api.get(`/platform/recall-locks/${lock.lock_id}/stock-count`);
      setData(res);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not load stock counts'); setOpen(false); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" className="pc-btn" disabled={!online && !open} onClick={toggle} data-testid={`pc-recall-stock-btn-${lock.lock_id}`}>
        {busy ? 'Counting…' : open ? 'Hide stock' : 'View stock'}
      </button>
      {open && data && (
        <div style={{ marginTop: '.5rem' }} data-testid={`pc-recall-stock-${lock.lock_id}`}>
          <p className="pc-muted" style={{ fontSize: '.72rem' }} data-testid={`pc-recall-stock-total-${lock.lock_id}`}>
            {data.total_units} unit{data.total_units === 1 ? '' : 's'} still on hand across {data.pharmacies.length} pharmac{data.pharmacies.length === 1 ? 'y' : 'ies'} — plan returns.
          </p>
          {data.pharmacies.length > 0 && (
            <table className="pc-table" style={{ marginTop: '.3rem' }}>
              <thead><tr><th>Pharmacy</th><th className="pc-num">Units held</th></tr></thead>
              <tbody>{data.pharmacies.map((p) => (
                <tr key={p.tenant_id} data-testid={`pc-recall-stock-row-${lock.lock_id}-${p.tenant_id}`}>
                  <td>{p.pharmacy}</td><td className="pc-num">{p.units}</td>
                </tr>))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </>
  );
};

const Locks = ({ rows, onChange }) => {
  const online = useOnlineStatus();
  const lift = async (l) => {
    if (!window.confirm(`Lift the recall lock on ${l.product_label || 'this medicine'} ${l.batch_label ? `batch ${l.batch_label}` : '(all batches)'}? Pharmacies will be able to sell it again.`)) return;
    try { await api.post(`/platform/recall-locks/${l.lock_id}/lift`); toast.success('Recall lock lifted'); onChange(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not lift the lock'); }
  };
  return rows.length === 0 ? null : (
    <div className="pc-card pc-scroll" style={{ marginTop: '1rem' }} data-testid="pc-recall-locks">
      <p className="pc-muted">Blocked from sale at every pharmacy</p>
      <table className="pc-table">
        <thead><tr><th>Medicine</th><th>Batch</th><th className="pc-num">Products flagged</th><th>Since</th><th>Stock still held</th><th /></tr></thead>
        <tbody>{rows.map((l) => (
          <tr key={l.lock_id} data-testid={`pc-recall-lock-${l.lock_id}`}>
            <td>{l.product_label || '—'}</td><td className="pc-mono">{l.batch_label || 'All batches'}</td>
            <td className="pc-num">{l.products_marked ?? 0}</td><td>{when(l.created_at)}</td>
            <td><StockCount lock={l} /></td>
            <td><button type="button" className="pc-btn" disabled={!online} onClick={() => lift(l)} data-testid={`pc-recall-lock-lift-${l.lock_id}`}>Lift lock</button></td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
};

const Campaigns = ({ rows }) => rows.length === 0 ? null : (
  <div className="pc-card pc-scroll" style={{ marginTop: '1rem' }} data-testid="pc-recall-campaigns">
    <p className="pc-muted">Recent recall texts</p>
    <table className="pc-table">
      <thead><tr><th>Medicine</th><th>Batch</th><th className="pc-num">Recipients</th><th className="pc-num">Sent</th><th className="pc-num">Failed</th><th>Status</th><th>When</th></tr></thead>
      <tbody>{rows.map((c) => (
        <tr key={c.campaign_id} data-testid={`pc-recall-campaign-${c.campaign_id}`}>
          <td>{c.product_label || '—'}</td><td className="pc-mono">{c.batch_label || '—'}</td>
          <td className="pc-num">{c.recipients}</td><td className="pc-num">{c.sent}</td><td className="pc-num" style={c.failed ? { color: '#ff9b9b' } : undefined}>{c.failed}</td>
          <td data-testid={`pc-recall-campaign-status-${c.campaign_id}`}>
            {c.status === 'done' && c.failed && !c.sent ? 'all failed' : c.status}
            {c.last_error && <div className="pc-muted" style={{ fontSize: '.7rem' }}>{c.last_error} · see SMS log</div>}
          </td><td>{when(c.created_at)}</td>
        </tr>))}
      </tbody>
    </table>
  </div>
);

export const RecallSmsBar = ({ terms, autoOpen = false }) => {
  const online = useOnlineStatus();
  const [open, setOpen] = useState(false);
  const [autoDone, setAutoDone] = useState(false);
  useEffect(() => { if (autoOpen && terms && !autoDone) { setOpen(true); setAutoDone(true); } }, [autoOpen, terms, autoDone]);
  const [rows, setRows] = useState([]);
  const [locks, setLocks] = useState([]);
  const load = useCallback(() => api.get('/platform/patients/recall/campaigns')
    .then(({ data }) => { setRows(data.campaigns); setLocks(data.locks || []); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!rows.some((c) => c.status === 'sending' && Date.now() - new Date(c.created_at) < 15 * 60e3)) return undefined;
    const t = setTimeout(load, 4000);
    return () => clearTimeout(t);
  }, [rows, load]);

  return (
    <>
      <div style={{ marginTop: '1rem' }}>
        <OnlineOnlyNotice feature="Recall texts, locks and stock counts" dark testId="pc-recall-offline-notice" />
      </div>
      {terms && (
        <button type="button" className="pc-btn pc-btn-primary" style={{ marginTop: '1rem' }} disabled={!online} onClick={() => setOpen(true)} data-testid="pc-recall-sms-btn">
          <MessageSquareWarning className="h-4 w-4 inline" /> Text these patients
        </button>
      )}
      <Locks rows={locks} onChange={load} />
      <Campaigns rows={rows} />
      {open && <RecallSmsDialog terms={terms} onClose={() => setOpen(false)} onSent={load} />}
    </>
  );
};
